import React, { useState, useRef, useEffect } from "react";
import Draggable from "react-draggable";
import {
  Form,
  Button,
  TreeSelect,
  Input,
  Select,
  Tooltip,
  Space,
  message,
  Row,
  Col,
  Switch,
  Spin,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { fetchMqtt } from "../../IotGateWay/Service/iotGateWayService";
import { fetchMachineById, postMachine, putMachine } from "../Service/machine";
import { fetchOrganization } from "../../Organization/Service/organization";

const buildTreeData = (obj, prefix = "") =>
  Object.entries(obj).map(([key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "object" && value !== null) {
      return {
        title: key,
        value: newKey, // ✅ full path
        children: buildTreeData(value, newKey),
      };
    }
    return { title: key, value: newKey }; // ✅ full path
  });

const operatorOptions = ["==", ">", "<", ">=", "<="];

const MachineForm = ({ mode }) => {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(false);
  const [jsonValid, setJsonValid] = useState(false);
  const [rawJson, setRawJson] = useState("{}");
  const [parsedJson, setParsedJson] = useState({});
  const [scriptCode, setScriptCode] = useState("");
  const [scriptResult, setScriptResult] = useState("");
  const [images, setImages] = useState([]);
  const [tags, setTags] = useState([]);
  const tagRefs = useRef([]);
  const [mqttList, setMqttList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [firmList, setFirmList] = useState([]); // list of firms
  const [selectedFirm, setSelectedFirm] = useState(null); // selected { firm, uuid }
  const [firmLoading, setFirmLoading] = useState(false);
  const [editData, setEditData] = useState(null);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // Handle JSON input changes
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setRawJson(value);

    try {
      const parsed = JSON.parse(value);
      setParsedJson(
        typeof parsed === "object" && parsed !== null ? parsed : {}
      );
      setJsonValid(typeof parsed === "object" && parsed !== null);
    } catch {
      setParsedJson({});
      setJsonValid(false);
    }
  };

  // Handle image uploads
  const handleUpload = (e) => {
    if (!jsonValid) return;
    const files = Array.from(e.target.files || []);

    const readers = files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    );

    Promise.all(readers).then((base64Imgs) =>
      setImages((prev) => [...prev, ...base64Imgs])
    );
    e.target.value = null;
  };

  // Add tag on image click
  const handleImageClick = (e, imgIndex) => {
    if (mode === "view" || !jsonValid) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;

    setTags((prev) => [
      ...prev,
      {
        imgIndex,
        anchorXPercent: xPercent,
        anchorYPercent: yPercent,
        xPercent: ((e.clientX - rect.left + 40) / rect.width) * 100,
        yPercent: ((e.clientY - rect.top + 40) / rect.height) * 100,
        key: null,
        displayName: "",
        conditions: [{ operator: "==", value: "", message: "" }],
      },
    ]);
  };

  const handleDragStop = (e, data, imgIndex, tagIndex, width, height) => {
    setTags((prev) =>
      prev.map((t, i) =>
        i === tagIndex && t.imgIndex === imgIndex
          ? {
              ...t,
              xPercent: (data.x / width) * 100,
              yPercent: (data.y / height) * 100,
            }
          : t
      )
    );
  };

  const handleKeyChange = (tagIndex, value) =>
    setTags((prev) =>
      prev.map((t, i) => (i === tagIndex ? { ...t, key: value } : t))
    );

  const handleDisplayNameChange = (tagIndex, value) =>
    setTags((prev) =>
      prev.map((t, i) => (i === tagIndex ? { ...t, displayName: value } : t))
    );

  const handleAddCondition = (tagIndex) =>
    setTags((prev) =>
      prev.map((t, i) =>
        i === tagIndex
          ? {
              ...t,
              conditions: [
                ...t.conditions,
                { operator: "==", value: "", message: "" },
              ],
            }
          : t
      )
    );

  const handleConditionChange = (tagIndex, condIndex, field, value) =>
    setTags((prev) =>
      prev.map((t, i) =>
        i === tagIndex
          ? {
              ...t,
              conditions: t.conditions.map((c, j) =>
                j === condIndex ? { ...c, [field]: value } : c
              ),
            }
          : t
      )
    );

  const handleDeleteCondition = (tagIndex, condIndex) =>
    setTags((prev) =>
      prev.map((t, i) =>
        i === tagIndex
          ? { ...t, conditions: t.conditions.filter((_, j) => j !== condIndex) }
          : t
      )
    );

  const handleDeleteTag = (tagIndex) => {
    setTags((prev) => prev.filter((_, i) => i !== tagIndex));
    tagRefs.current = tagRefs.current.filter((_, i) => i !== tagIndex);
  };

  const handleDeleteImage = (imgIndex) => {
    setImages((prev) => prev.filter((_, i) => i !== imgIndex));
    setTags((prev) =>
      prev
        .filter((t) => t.imgIndex !== imgIndex)
        .map((t) =>
          t.imgIndex > imgIndex ? { ...t, imgIndex: t.imgIndex - 1 } : t
        )
    );
  };

  const saveData = async (data) => {
    setIsLoading(true);
    try {
      let res;
      if (editData) res = await putMachine(data, editData.uuid);
      else res = await postMachine(data);

      messageApi.open({
        type: "success",
        content: `Machine ${editData ? "updated" : "added"} successfully`,
      });

      navigate({ pathname: "/configuration/machine" });
      form.resetFields();
    } catch {
      messageApi.open({ type: "error", content: "Error saving machine" });
    } finally {
      setIsLoading(false);
    }
  };

  const onFinish = (data) => {
    if (!jsonValid) {
      alert("Valid JSON is required!");
      return;
    }

    if (!scriptCode.trim()) {
      alert("Script is required!");
      return;
    }

    if (images.length === 0) {
      alert("Please upload at least one image!");
      return;
    }

    if (tags.length === 0) {
      alert("Please add at least one tag!");
      return;
    }

    for (let i = 0; i < tags.length; i++) {
      const tag = tags[i];
      if (!tag.key) {
        alert(`Tag ${i + 1}: Please select a key`);
        return;
      }
      if (!tag.displayName.trim()) {
        alert(`Tag ${i + 1}: Please enter a Display Name`);
        return;
      }
      for (let j = 0; j < tag.conditions.length; j++) {
        const cond = tag.conditions[j];
        if (!cond.operator || !cond.value.trim() || !cond.message.trim()) {
          alert(`Tag ${i + 1}, Condition ${j + 1}: Please fill all fields`);
          return;
        }
      }
    }

    const submissionData = {
      ...data,
      sampleJson: rawJson,
      script: scriptCode,
      images,
      status: data.status ? "ACTIVE" : "INACTIVE",
      tags,
      firm: { uuid: data.firm.uuid },
    };

    saveData(submissionData);
  };

  const fetchMqttList = async () => {
    setLoading(true);
    try {
      const res = await fetchMqtt();
      setMqttList(res.data.content || []);
    } catch {
      messageApi.open({
        type: "error",
        content: "Network error fetching MQTT list",
      });
    }
    setLoading(false);
  };

  // Utility: convert flat list with "parent" references into tree data
  const buildOrgTree = (list) => {
    const map = new Map();
    const roots = [];

    // Initialize all as tree nodes
    list.forEach((org) => {
      map.set(org.uuid, {
        title: org.name,
        value: org.uuid,
        key: org.uuid,
        children: [],
      });
    });

    // Build hierarchy
    list.forEach((org) => {
      if (org.parent && map.has(org.parent.uuid)) {
        map.get(org.parent.uuid).children.push(map.get(org.uuid));
      } else {
        roots.push(map.get(org.uuid));
      }
    });

    return roots;
  };

  // ✅ Convert organization list into hierarchical TreeSelect structure
  const flattenFirmList = (data) => {
    if (!Array.isArray(data)) return [];

    // Create a lookup table of all orgs
    const map = {};
    data.forEach((item) => {
      map[item.uuid] = { ...item, children: [] };
    });

    const roots = [];

    // Link children to their parents
    data.forEach((item) => {
      if (item.parent && item.parent.uuid) {
        const parent = map[item.parent.uuid];
        if (parent) {
          parent.children.push(map[item.uuid]);
        } else {
          roots.push(map[item.uuid]); // parent not found → treat as root
        }
      } else {
        roots.push(map[item.uuid]);
      }
    });

    // Convert to AntD TreeSelect format
    const buildTree = (nodes) =>
      nodes.map((n) => ({
        title: n.name,
        value: n.uuid,
        key: n.uuid,
        children: n.children.length ? buildTree(n.children) : undefined,
      }));

    return buildTree(roots);
  };

  const fetchFirmList = async () => {
    setFirmLoading(true);
    try {
      const res = await fetchOrganization(); // replace with your firm API
      const orgList = res.data || [];
      const tree = buildOrgTree(orgList);
      setFirmList(tree);
    } catch (err) {
      messageApi.open({ type: "error", content: "Error fetching firm list" });
    } finally {
      setFirmLoading(false);
    }
  };

  useEffect(() => {
    fetchFirmList();
  }, []);

  const fetchMachineByUuid = async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const res = await fetchMachineById(uuid);
      if (res?.data?.data) {
        const data = res.data.data;
        setEditData(data);

        // ✅ Populate the form properly
        form.setFieldsValue({
          assetName: data.assetName,
          iotGatewayUuid: data.ioTGateway.uuid,
          mqttPrefix: data.mqttPrefix,
          description: data.description || "",
          status: data.status === "ACTIVE", // convert string to boolean
          firm: { uuid: data.firm?.uuid }, // set nested form field
        });

        // ✅ Handle selected firm (for TreeSelect display)
        setSelectedFirm(data.firm || null);

        // ✅ Parse and validate JSON
        const jsonStr = data.sampleJson || "{}";
        setRawJson(jsonStr);
        try {
          const parsed = JSON.parse(jsonStr);
          setParsedJson(parsed);
          setJsonValid(true);
        } catch {
          setParsedJson({});
          setJsonValid(false);
        }

        // ✅ Script and execution
        setScriptCode(data.script || "");

        // ✅ Images and tags (wait for render)
        setImages(data.images || []);
        setTimeout(() => {
          setTags(data.tags || []);
        }, 200);
      } else {
        message.error("Invalid response from server");
      }
    } catch (err) {
      console.error("Error fetching machine:", err);
      message.error("Error fetching machine data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMqttList();
    if (uuid) fetchMachineByUuid();
  }, [mode, uuid]);

  useEffect(() => {
    if (!jsonValid || !scriptCode.trim()) {
      setScriptResult("");
      return;
    }
    try {
      const parsed = JSON.parse(rawJson);
      const fn = new Function("sampleData", scriptCode);
      const result = fn(parsed);
      setScriptResult(JSON.stringify(result, null, 2));
    } catch (err) {
      setScriptResult(`⚠️ Error: ${err.message}`);
    }
  }, [rawJson, scriptCode, jsonValid]);

  const treeData = buildTreeData(parsedJson);

  return (
    <Spin spinning={isLoading}>
      {contextHolder}
      <h3>Machine Configuration</h3>
      <br />
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col>
            <Form.Item
              label="Machine name"
              name="assetName"
              rules={[{ required: true, message: "Machine name is required" }]}
            >
              <Input disabled={mode === "view"} style={{ width: 300 }} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              label="Prefix"
              name="mqttPrefix"
              rules={[{ required: true, message: "Prefix is required" }]}
            >
              <Input disabled={mode === "view"} style={{ width: 300 }} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              label="Organization"
              name={["firm", "uuid"]}
              rules={[
                { required: true, message: "Please select a organization" },
              ]}
            >
              <TreeSelect
                placeholder="Select Organization"
                disabled={mode === "view"}
                style={{ width: 300 }}
                loading={firmLoading}
                treeData={firmList}
                value={selectedFirm?.uuid}
                onChange={(uuid) => {
                  const firm = flattenFirmList(firmList).find(
                    (f) => f.uuid === uuid
                  );
                  setSelectedFirm(
                    firm ? { firm: firm.name, uuid: firm.uuid } : null
                  );
                }}
                treeDefaultExpandAll
              />
            </Form.Item>
          </Col>

          <Col>
            <Form.Item
              label="MQTT"
              name="iotGatewayUuid"
              rules={[{ required: true, message: "MQTT is required" }]}
            >
              <Select
                showSearch
                disabled={mode === "view"}
                placeholder="Select IoT Gateway"
                loading={loading}
                style={{ width: 300 }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.label.toLowerCase().includes(input.toLowerCase())
                }
                options={mqttList?.map((item) => ({
                  label: item.name || item.clientId || item.uuid,
                  value: item.uuid,
                }))}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch
                disabled={mode === "view"}
                defaultChecked
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          </Col>
          <Col flex={1}>
            <Form.Item label="Description" name="description">
              <Input.TextArea
                disabled={mode === "view"}
                autoSize={{ minRows: 3, maxRows: 10 }}
                style={{ width: "100%" }}
                placeholder="Enter machine description"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Sample JSON */}
        <Form.Item label="Sample JSON (Parameters)">
          <Input.TextArea
            value={rawJson}
            onChange={handleJsonChange}
            disabled={mode === "view"}
            autoSize={{ minRows: 5, maxRows: 30 }}
            style={{ width: "100%", fontFamily: "monospace" }}
          />
          {!jsonValid && <span style={{ color: "red" }}>Invalid JSON!</span>}
        </Form.Item>

        {/* Script */}
        <Form.Item
          label="function Script (sampleData) {"
          tooltip="Write inside function body"
        >
          <Input.TextArea
            value={scriptCode}
            onChange={(e) => setScriptCode(e.target.value)}
            disabled={mode === "view"}
            autoSize={{ minRows: 5, maxRows: 30 }}
            style={{ width: "100%", fontFamily: "monospace" }}
          />
          <div>{"}"}</div>
        </Form.Item>

        <Form.Item label="Script Execution Result">
          <Input.TextArea
            value={scriptResult}
            autoSize={{ minRows: 5, maxRows: 30 }}
            style={{
              width: "100%",
              fontFamily: "monospace",
              background: "transparent",
            }}
            readOnly
          />
        </Form.Item>

        {/* Image upload and tags */}
        <Form.Item label="Upload Images">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            disabled={mode === "view" || !jsonValid}
          />
        </Form.Item>

        {/* Render images and tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {images.map((src, imgIndex) => (
            <div
              key={imgIndex}
              style={{
                border: "1px solid #ddd",
                width: "100%",
                marginBottom: 8,
              }}
            >
              <Space
                style={{
                  width: "100%",
                  justifyContent: "flex-end",
                  padding: 5,
                }}
              >
                <Tooltip title="Delete image">
                  <Button
                    type="text"
                    danger
                    size="small"
                    disabled={mode === "view"}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(imgIndex);
                    }}
                  >
                    ❌
                  </Button>
                </Tooltip>
              </Space>

              <div style={{ position: "relative" }}>
                <img
                  src={src}
                  alt={`uploaded-${imgIndex}`}
                  data-idx={imgIndex}
                  style={{ width: "100%", height: "auto" }}
                  onClick={(e) => handleImageClick(e, imgIndex)}
                />

                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  {tags
                    .filter((t) => t.imgIndex === imgIndex)
                    .map((tag, idx) => {
                      const imgEl = document.querySelector(
                        `img[data-idx="${imgIndex}"]`
                      );
                      const rect = imgEl?.getBoundingClientRect();
                      const width = rect?.width || 1;
                      const height = rect?.height || 1;
                      const anchorX = (tag.anchorXPercent / 100) * width;
                      const anchorY = (tag.anchorYPercent / 100) * height;
                      const x = (tag.xPercent / 100) * width;
                      const y = (tag.yPercent / 100) * height;
                      return (
                        <line
                          key={idx}
                          x1={anchorX}
                          y1={anchorY}
                          x2={x + 24}
                          y2={y + 24}
                          stroke="red"
                          strokeWidth="2"
                        />
                      );
                    })}
                </svg>

                {tags
                  .map((t, idx) => ({ ...t, tagIndex: idx }))
                  .filter((t) => t.imgIndex === imgIndex)
                  .map((tag) => {
                    if (!tagRefs.current[tag.tagIndex])
                      tagRefs.current[tag.tagIndex] = React.createRef();
                    const nodeRef = tagRefs.current[tag.tagIndex];
                    const imgEl = document.querySelector(
                      `img[data-idx="${imgIndex}"]`
                    );
                    const rect = imgEl?.getBoundingClientRect();
                    const width = rect?.width || 1;
                    const height = rect?.height || 1;
                    const x = (tag.xPercent / 100) * width;
                    const y = (tag.yPercent / 100) * height;

                    return (
                      <Draggable
                        key={tag.tagIndex}
                        nodeRef={nodeRef}
                        position={{ x, y }}
                        bounds="parent"
                        disabled={mode === "view"}
                        onStop={(e, data) =>
                          handleDragStop(
                            e,
                            data,
                            imgIndex,
                            tag.tagIndex,
                            width,
                            height
                          )
                        }
                      >
                        <div
                          ref={nodeRef}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            transform: "translate(-50%, -50%)",
                            background: "#fff",
                            border: "1px solid #999",
                            padding: 6,
                            borderRadius: 6,
                            cursor: mode === "view" ? "default" : "move",
                            minWidth: 280,
                          }}
                        >
                          <TreeSelect
                            style={{ width: "100%" }}
                            value={tag.key}
                            treeData={treeData}
                            placeholder="Select key"
                            onChange={(val) =>
                              handleKeyChange(tag.tagIndex, val)
                            }
                            treeDefaultExpandAll
                            allowClear={false}
                            disabled={mode === "view"}
                          />
                          <Input
                            style={{ width: "100%", marginTop: 4 }}
                            value={tag.displayName}
                            placeholder="Enter Display Name"
                            readOnly={mode === "view"}
                            onChange={(e) =>
                              handleDisplayNameChange(
                                tag.tagIndex,
                                e.target.value
                              )
                            }
                          />

                          {tag.conditions.map((cond, condIdx) => (
                            <div
                              key={condIdx}
                              style={{
                                display: "flex",
                                gap: 4,
                                alignItems: "center",
                                marginTop: 4,
                              }}
                            >
                              <Select
                                style={{ width: 70 }}
                                value={cond.operator}
                                disabled={mode === "view"}
                                onChange={(val) =>
                                  handleConditionChange(
                                    tag.tagIndex,
                                    condIdx,
                                    "operator",
                                    val
                                  )
                                }
                                options={operatorOptions.map((op) => ({
                                  label: op,
                                  value: op,
                                }))}
                              />
                              <Input
                                style={{ width: 80 }}
                                value={cond.value}
                                placeholder="Value"
                                readOnly={mode === "view"}
                                onChange={(e) =>
                                  handleConditionChange(
                                    tag.tagIndex,
                                    condIdx,
                                    "value",
                                    e.target.value
                                  )
                                }
                              />
                              <Input
                                style={{ width: 120 }}
                                value={cond.message}
                                placeholder="Message"
                                readOnly={mode === "view"}
                                onChange={(e) =>
                                  handleConditionChange(
                                    tag.tagIndex,
                                    condIdx,
                                    "message",
                                    e.target.value
                                  )
                                }
                              />
                              <Button
                                type="text"
                                danger
                                onClick={() =>
                                  handleDeleteCondition(tag.tagIndex, condIdx)
                                }
                                disabled={mode === "view"}
                              >
                                ✖
                              </Button>
                            </div>
                          ))}

                          <Button
                            type="dashed"
                            size="small"
                            onClick={() => handleAddCondition(tag.tagIndex)}
                            style={{ marginTop: 4 }}
                            disabled={mode === "view"}
                          >
                            + Add Row
                          </Button>
                          <Button
                            type="text"
                            danger
                            onClick={() => handleDeleteTag(tag.tagIndex)}
                            style={{ marginTop: 4 }}
                            disabled={mode === "view"}
                          >
                            Delete Tag
                          </Button>
                        </div>
                      </Draggable>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 12,
          }}
        >
          <Button
            onClick={() => {
              navigate({ pathname: "/configuration/machine", replace: true });
              form.resetFields();
            }}
          >
            Cancel
          </Button>
          {mode === "view" || (
            <Button type="primary" htmlType="submit">
              Submit Tags
            </Button>
          )}
        </div>
      </Form>
    </Spin>
  );
};

export default MachineForm;
